/**
 * REFERRAL CARD — one extra card on the 4-5★ thank-you screen.
 *
 * It issues a voucher through the guest-side path the product already has
 * (`POST /api/public/loyal-review/{card_id}/issue`, email + mandatory T&C,
 * exactly as the Loyal Review flow does) and then shows the EXISTING shareable
 * voucher link `/voucher/{code}` so a happy patient can pass it to a friend.
 *
 * No patient database, no referrer tracking, no new setting, and NO
 * medical-only voucher rule: whatever voucher the clinic configured is what
 * gets issued.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2, Share2, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = (typeof window !== 'undefined' ? window.location.origin : '');

export default function MedicalReferralCard({ cardId, feedbackId }) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [code, setCode] = useState('');
    const [copied, setCopied] = useState(false);

    const link = code ? `${window.location.origin}/voucher/${code}` : '';

    const issue = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/public/loyal-review/${cardId}/issue`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email.trim(),
                    feedback_id: feedbackId || undefined,
                    tc_accepted: true,
                    tc_language: (i18n.language || 'en').slice(0, 2),
                }),
            });
            const d = await res.json().catch(() => ({}));
            if (!res.ok || !d.code) throw new Error(d.detail || `HTTP ${res.status}`);
            setCode(d.code);
        } catch (e) {
            setError(t('reviewShield.referral.error', 'That did not go through. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    const share = async () => {
        try {
            if (navigator.share) {
                await navigator.share({ title: t('reviewShield.referral.shareTitle', 'A gift for you'), url: link });
                return;
            }
        } catch (e) { /* the patient dismissed the sheet — fall through to copy */ }
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (e) { /* clipboard unavailable — the link is on screen anyway */ }
    };

    return (
        <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4" data-testid="referral-card">
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                    <Gift className="w-4.5 h-4.5 text-emerald-400" />
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-white" data-testid="referral-card-title">
                        {t('reviewShield.referral.title', 'Pass it on')}
                    </p>
                    <p className="text-xs text-slate-300 mt-1" data-testid="referral-card-body">
                        {t('reviewShield.referral.body', 'Send a friend the same gift from our clinic.')}
                    </p>

                    {!open && !code && (
                        <Button
                            onClick={() => setOpen(true)}
                            className="mt-3 h-10 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm"
                            data-testid="referral-card-start-btn"
                        >
                            {t('reviewShield.referral.cta', 'Get a gift to share')}
                        </Button>
                    )}

                    {open && !code && (
                        <div className="mt-3 space-y-2.5">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('reviewShield.referral.emailPlaceholder', 'your@email.com')}
                                className="w-full rounded-lg bg-slate-900/60 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500"
                                data-testid="referral-card-email"
                            />
                            <label className="flex items-start gap-2 text-xs text-slate-300">
                                <input
                                    type="checkbox"
                                    checked={accepted}
                                    onChange={(e) => setAccepted(e.target.checked)}
                                    className="mt-0.5"
                                    data-testid="referral-card-terms"
                                />
                                <span>{t('reviewShield.referral.terms', 'I accept the voucher terms and conditions.')}</span>
                            </label>
                            {error && <p className="text-xs text-rose-300" data-testid="referral-card-error">{error}</p>}
                            <Button
                                onClick={issue}
                                disabled={busy || !accepted || !email.includes('@')}
                                className="h-10 w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-40"
                                data-testid="referral-card-issue-btn"
                            >
                                {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                {t('reviewShield.referral.issue', 'Create the gift')}
                            </Button>
                        </div>
                    )}

                    {code && (
                        <div className="mt-3 space-y-2.5" data-testid="referral-card-ready">
                            <p className="font-mono text-lg text-emerald-400" data-testid="referral-card-code">{code}</p>
                            <p className="text-xs text-slate-400 break-all" data-testid="referral-card-link">{link}</p>
                            <Button
                                onClick={share}
                                className="h-10 w-full rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm"
                                data-testid="referral-card-share-btn"
                            >
                                {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                                {copied
                                    ? t('reviewShield.referral.copied', 'Link copied')
                                    : t('reviewShield.referral.share', 'Share the link')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
